/**
 * Entity Type Registry - Central configuration for CRM entity types.
 *
 * This registry defines all metadata, display logic, and type-specific components
 * for each CRM entity type. Adding a new entity type only requires adding an entry here.
 */

import type { ComponentType } from 'react';

import type { EntityTypeName } from '@/types/crm';

import { _setEntityTypeConfigGetter } from '../../components/badges/hoverBodyDispatch';
import type { CrmObject, RecordAction } from '../../core/types';
import { getDisplayName } from '../../utils/getDisplayName';
import type { TimelineConfig } from '../timeline/types';
import { AGENT_ENTITY_TYPES } from './entityTypeRegistry.agent';
import { AUTOMATION_ENTITY_TYPES } from './entityTypeRegistry.automation';
import { CORE_ENTITY_TYPES } from './entityTypeRegistry.core';
import { CREDENTIAL_ENTITY_TYPES } from './entityTypeRegistry.credentials';
import { SYSTEM_ENTITY_TYPES } from './entityTypeRegistry.system';
import { WEB_ENTITY_TYPES } from './entityTypeRegistry.web';
import type { TypeSpecificSectionEntry } from './relatedSections';

/**
 * A field entry in the detail view's ordered field list.
 * Discriminated union allows intermixing properties, associations, and timestamps.
 */
export type DetailField =
  | { type: 'property'; key: string }
  | {
      type: 'association';
      /** The association type name — what a write emits. */
      key: string;
      /**
       * Set when this field is ONE per-target-type subset of a kind-based rule
       * (see `utils/associationTargetSubsets.ts`). A kind-based rule resolves to
       * every type of its target kind, so it is rendered as one field per
       * resolved type — each labelled with that type's plural label and picking
       * only from it. Absent for a rule that names a single target type, which
       * is rendered as one field exactly as before.
       */
      targetEntityTypeName?: string;
    }
  | { type: 'timestamp'; key: 'ahoy_created_at' | 'ahoy_updated_at' };

/**
 * Stable per-field identity for a DetailField.
 *
 * Two subsets of the same kind-based rule share an `association_type_name`, so
 * the association type alone is no longer unique — form state, save handlers
 * and React keys must use this instead.
 */
export function detailFieldKey(field: DetailField): string {
  if (field.type === 'association' && field.targetEntityTypeName) {
    return `${field.key}::${field.targetEntityTypeName}`;
  }
  return field.key;
}

/** Tab identifiers for the full-page record view. */
export type FullPageTabId =
  | 'overview'
  | 'activity'
  | 'contacts' // list of `ahoy_contact` entities associated with the record (e.g. on a deal)
  | 'emails'
  | 'meetings' // list of `ahoy_meeting` entities associated with the record
  | 'notes'
  | 'tasks' // list of `ahoy_task` entities associated with the record
  | 'files'
  | 'associations'
  | 'prep' // conditional: meetings
  | 'intelligence'; // conditional: contact/company under research_feature flag

/** Source of the AI summary shown on a record. */
export type AISummarySourceKind =
  | 'research' // contacts, companies — via useResearch
  | 'meeting_notes' // meetings — via ahoy_meeting_notes.ahoy_summary
  | 'none'; // default — no summary available

/** Discriminator for computed highlight resolvers (see lib/crm/record/highlights/ComputedHighlightCard). */
export type ComputedHighlightKind =
  | 'company_location' // concat city/region/country from properties
  | 'meeting_duration' // ahoy_end_time - ahoy_start_time → "30 min"
  | 'company_open_deal_value' // sum ahoy_amount over all deals associated to this company (closed-stage filtering not available; rendered as "Total deal value")
  | 'contact_last_met' // most recent PAST meeting (max ahoy_start_time strictly before the current-hour cutoff) associated to this contact
  | 'next_interaction' // min ahoy_start_time over meetings associated to this entity where start_time >= now (contact → ahoy_meeting_to_contact, company → ahoy_meeting_to_company)
  | 'contact_touchpoints_30d' // count of emails + meetings in the last 30 days; calls added once that entity type exists
  | 'meeting_prep_status' // whether a meeting-prep research record exists
  | 'meeting_summary_status'; // whether ahoy_meeting_notes.ahoy_summary exists

/**
 * Highlight card definition. Each kind maps to existing data primitives.
 * Consumed by the Highlights grid (downstream issue); declared here as the
 * type foundation — no entity entry populates `highlights` in this issue.
 */
export type HighlightDefinition =
  | { kind: 'property'; label: string; propertyName: string }
  | { kind: 'association'; label: string; associationType: string }
  | {
      kind: 'association_count';
      label: string;
      associationType: string;
      /**
       * When set, the count tile becomes a button that switches the full-page
       * record to this tab (e.g. the "Contacts" count → the Contacts tab).
       * Only meaningful on the full-page record, which is tab-driven.
       */
      linkToTab?: FullPageTabId;
    }
  | {
      kind: 'entity_count';
      label: string;
      filter: {
        entityType: EntityTypeName | 'event';
        associationType: string;
        sinceDays?: number;
      };
    }
  | {
      kind: 'last_event';
      label: string;
      eventTypeNames?: string[];
      eventGroup?: string;
    }
  | { kind: 'last_property_update'; label: string; propertyNames?: string[] }
  | { kind: 'computed'; label: string; compute: ComputedHighlightKind };

/**
 * A header action button shown on the full-page record header (e.g. Gmail,
 * LinkedIn, Website). `getHref` returns null when the action is unavailable
 * for the given object; `guard` (optional) is a pre-check (e.g. URL allowlist).
 */
export interface HeaderActionDefinition {
  id: string;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: 'true' }>;
  getHref: (object: CrmObject) => string | null;
  getLabel: (object: CrmObject) => string;
  guard?: (object: CrmObject) => boolean;
}

/**
 * Configuration for inline create functionality.
 */
export interface EntityTypeCreateConfig {
  /** Properties to show in the create form */
  createProperties: string[];
  /** Associations to show in the create form */
  createAssociations: string[];
  /** Function to parse search text into initial property values */
  parseSearchText: (text: string) => Record<string, unknown>;
}

/**
 * A tab in a tabbed slideout body (`EntityTypeConfig.slideoutTabs`).
 * - overview — type-specific sections + the editable field list (SaveBar
 *   intact) + the long-form Documentation section (`components.documentation`)
 * - code — the record's effective script; hidden when there is none
 * - testcases — read-only view of ahoy_script.fixtures; hidden when the record has no non-empty fixtures array
 * - runs — a LOCKED run-record view (`ahoy_automation_ran` + legacy
 *   `ahoy_event_processed` rows; not the general timeline)
 */
export type SlideoutTabId = 'overview' | 'code' | 'testcases' | 'runs';

/**
 * Configuration for a entity type.
 */
export interface EntityTypeConfig {
  /** Display name (singular) */
  displayName: string;

  /** Display name (plural) */
  pluralDisplayName: string;

  /** Primary color for badges, accents, etc. */
  color: string;

  /** CSS gradient for banner (linear-gradient format) */
  bannerGradient: string;

  /** Function to get display name from CrmObject */
  getDisplayName: (object: CrmObject) => string;

  /** Entity configuration */
  timelineConfig?: TimelineConfig;

  /**
   * Whether the slideout Timeline section opens expanded by default.
   * Defaults to expanded (true) when omitted. Set false to open the
   * Timeline collapsed (meeting / meeting-notes / custom-entity panels).
   */
  timelineDefaultExpanded?: boolean;

  /**
   * Where the type-specific section renders relative to the overview/properties
   * section in the slideout. Defaults to `'after-overview'`.
   *
   * - `'after-overview'` (default) — below the properties section
   * - `'before-overview'` — above the properties section (used by booking
   *   types so the booking URLs appear above the editable properties)
   */
  typeSpecificPosition?: 'before-overview' | 'after-overview';

  /** Route patterns (optional — omit for non-navigable entity types) */
  routes?: {
    list: string;
    detail: string;
  };

  /** Full-page detail route pattern (optional - for expanded record view) */
  fullPageRoute?: string;

  /** Override the back-navigation route and label on the full-page view.
   *  When omitted, defaults to routes.list / pluralDisplayName. */
  backNavigation?: {
    route: string;
    label: string;
  };

  /** Configuration for inline create functionality */
  createConfig?: EntityTypeCreateConfig;

  /**
   * Ordered field list for the detail/slideout view.
   * Intermixes properties, associations, and timestamps in exact display order.
   * When undefined, falls back to: all properties → all associations → timestamps.
   * Fields not in this list but present in schema are appended after the ordered fields.
   */
  detailFieldOrder?: DetailField[];

  /**
   * Properties that should always render in read-only mode, with placeholder text
   * when their value is empty. Keyed by property name → placeholder string.
   */
  emptyPlaceholders?: Record<string, string>;

  /**
   * Properties to hide from the detail/slideout view entirely.
   * Use this for fields that are stored but not useful to display
   * (e.g., redundant with another field, internal IDs, etc.).
   */
  hiddenProperties?: string[];

  /**
   * Association types to hide from the detail/slideout field list entirely —
   * the association counterpart of `hiddenProperties`. Use it when another
   * surface owns the relationship's rendering (the automation slideout's top
   * zone renders its webhook endpoints and creator, so field rows for them
   * would double-render). Applies to read AND edit mode, and to create-form
   * placeholder rows.
   */
  hiddenAssociations?: string[];

  /**
   * RecordMetaSection (the read-only creator/created metadata block)
   * renders in both the tabbed and non-tabbed slideout paths whenever
   * `enabled.timeline` is true — but the two paths react to this field
   * differently:
   * - Tabbed path: RecordBodyTabbedContent renders RecordMetaSection
   *   unconditionally (relying on RecordMetaSection's own defaults when
   *   `recordMeta` is unset). Setting this field only customizes `variant`
   *   and `creatorAssociationTypes` there — it does not enable the block.
   * - Non-tabbed path: RecordBody gates the block on `recordMeta` being set
   *   at all, at the foot of the overview. Setting this field enables the
   *   block for this path.
   * Slideout-only: the full-page record view does not use RecordBody, so this has
   * no effect there — a type reachable full-page that also hides its creator edges
   * would lose that provenance on the full page.
   * `variant` picks the layout ('inline' = one "Created by {name} on {date}" line).
   * `creatorAssociationTypes` lists the creator edges to resolve, in priority order.
   */
  recordMeta?: {
    variant?: 'rows' | 'inline';
    creatorAssociationTypes?: readonly string[];
  };

  /**
   * Property keys pulled out of the main EDIT-mode field list into a
   * collapsed-by-default Advanced section. Read mode has no counterpart:
   * the overview shows every filled field and keeps only empty properties
   * and the created/updated timestamps behind "View all values".
   */
  advancedFields?: string[];

  /**
   * Per-property custom edit renderers, keyed by property_type_name.
   * When present, OverviewEditSection uses this component instead of the
   * generic CrmFieldValueInput for that property in edit mode.
   *
   * Each component receives the same `{ value, onChange, disabled }` interface
   * that the create-form custom renderers use, so components can be shared
   * between create and edit surfaces (e.g. DurationField).
   */
  customPropertyEditors?: Record<
    string,
    ComponentType<{
      value: unknown;
      onChange: (next: unknown) => void;
      disabled: boolean;
    }>
  >;

  /** Per-property read-only display overrides, keyed by property_type_name.
   * Used for properties whose generic field_type (e.g. json) can't be
   * dispatched via PropertyValueLabel without affecting every such property.
   * The component owns its full row and returns null to render nothing. */
  customPropertyReadRenderers?: Record<
    string,
    ComponentType<{ value: unknown; orgId: number }>
  >;

  /**
   * When true, the record cannot be edited by users. Hides the edit button
   * in OverviewSection and action menu items (AddToCRM, delete, enrich) in
   * RecordHeader. Used for system-managed entities like meetings.
   */
  readOnly?: boolean;

  /**
   * When true, RecordBody does not render the generic TimelineSection
   * (or the `beforeTimeline` slot) for this entity type. Used when a
   * bespoke body already presents the record's events in its own form —
   * e.g. `ahoy_email_thread`, whose conversation view renders the same
   * email events as a readable thread, making the reverse-chronological
   * timeline a duplicate. The full-page Activity tab is unaffected: the
   * generic timeline remains available there.
   */
  hideTimelineSection?: boolean;

  /**
   * When true, RecordBody does not render the generic FilesSection for
   * this entity type, even when a file association resolves from the schema.
   * Used by ahoy_note: the task-kind slideout renders its own TaskAttachments
   * section instead, so plain notes and reminders must show no generic Files
   * section.
   */
  hideFilesSection?: boolean;

  /**
   * Explicit override for whether the RecordActionMenu (kebab → delete /
   * add-to-CRM / enrich) renders in the slideout/fullpage header. When
   * omitted the header defaults to `!readOnly`.
   *
   * Use this for read-only entity types that still want the standard
   * delete dropdown — e.g. `ahoy_note` is readOnly (NoteCard owns its own
   * inline pencil edit) but should expose delete through the header so it
   * matches every other deletable record.
   */
  showRecordActionMenu?: boolean;

  /**
   * When set, RecordBody renders an inline "Edit" button in the slideout
   * header (this string is its label, e.g. "Edit note") for entity types whose
   * `overview` override edits in place. Clicking it puts the override into edit
   * mode via the `editMode` / `onEditModeChange` props; the button hides while
   * editing. Only meaningful alongside a `components.overview` that accepts
   * those props. Omit for types that edit through the standard OverviewSection
   * (its own section-level Edit toggle) or not at all.
   */
  overviewHeaderEditLabel?: string;

  /**
   * Whether this type can be built and refined conversationally in builder
   * mode — chat on the left, the record in a panel on the right. Adds an
   * "Edit with Ahoy" action to the record menu.
   *
   * Opt in explicitly rather than deriving this from `entity_kind`. Several
   * automation-kind types (inbound webhooks, webhook secrets, Recall sessions)
   * are `internal` on `ahoy_property_value` and so cannot be edited by a user
   * or an agent at all — a kind check would offer an action that is guaranteed
   * to fail. Buildability is a capability, not a classification.
   */
  supportsBuilderMode?: boolean;

  /**
   * Tabbed slideout body. When set, the record view renders a two-zone
   * layout: the top zone stays basic (header, sub-header badges, summary
   * card) and everything else moves behind tabs. Declared tabs self-gate on
   * availability — Code hides without a script,
   * Runs respects the surface's timeline suppression — and the tab bar
   * itself disappears when only one tab survives (HorizontalTabs ≤1 rule).
   *
   * Notes, files, research and the GENERAL timeline do not render at all in
   * tabbed mode: Runs is a locked run-record view (`ahoy_automation_ran` +
   * legacy `ahoy_event_processed` rows), not the timeline with a filter applied.
   */
  slideoutTabs?: readonly SlideoutTabId[];

  /**
   * Whether to show the avatar in the record header. Defaults to true.
   * Set to false for entity types where avatars are not meaningful (e.g. meetings).
   */
  showAvatar?: boolean;

  /**
   * Header layout variant for the slideout.
   * - `'standard'` (default): Entity icon, avatar, action buttons, full identity section.
   * - `'compact'`: Minimal header — no icon, no avatar, no action buttons, just
   *   a lightweight label + title + sub-header + close. Used for meetings.
   */
  headerVariant?: 'standard' | 'compact';

  /**
   * Record actions for single-record views (slideout/full-page).
   *
   * NOTE: This field is a future extension point. RecordActionMenu currently
   * hardcodes delete (all types) and phone enrichment (contacts only). When
   * more registry-driven actions are needed, RecordActionMenu should be updated
   * to read this field and map action IDs to their handler implementations.
   *
   * @see RecordActionMenu for current hardcoded action implementation
   */
  recordActions?: RecordAction[];

  /**
   * Subtitle shown next to the display name on the full-page record header.
   * Return null (or omit the function) when the entity has no meaningful subtitle.
   */
  getSubtitle?: (object: CrmObject) => string | null;

  /** Header action buttons for the full-page record view. */
  headerActions?: HeaderActionDefinition[];

  /** Highlight cards for the record overview. Populated by a downstream issue. */
  highlights?: HighlightDefinition[];

  /** Ordered tabs for the full-page record view. Populated by a downstream issue. */
  tabs?: FullPageTabId[];

  /** Which AI-summary source applies to this entity type. Populated by a downstream issue. */
  aiSummarySource?: AISummarySourceKind;

  /**
   * Optional per-entity-type component overrides for slideout / record-view
   * sections. The consolidation goal is to move the hardcoded
   * `switch (entity_type_name)` blocks in SummarySection, SubHeaderSection,
   * TypeSpecificSection, and single-slideout.tsx into THIS map so adding a
   * new entity type with custom UI is a one-line registry change rather
   * than touching N files.
   *
   * Semantics per slot:
   *   - `undefined` (slot omitted)  → default rendering for that section
   *   - explicit `null`             → render NOTHING for that section
   *                                   (e.g. `summary: null` for `ahoy_note`,
   *                                   `contact`, `company`, `deal`)
   *   - a component                 → render this instead of the default
   *
   * Today only `summary` and `overview` are migrated — the rest of the
   * messy switches (sub-header, type-specific, hover-body) come in
   * follow-up PRs. Add more slots here as each switch is migrated.
   */
  components?: EntityTypeComponentOverrides;
}

/**
 * Component-override slots an entity type can register on the registry. See
 * the `components` field on EntityTypeConfig for the contract.
 *
 * Each slot's component receives at minimum `{ object: CrmObject }`. Slots
 * with additional context (e.g. callbacks supplied by the parent section)
 * extend the prop shape — type those at the slot, not on EntityTypeConfig,
 * so the slot definitions stay self-documenting.
 */
export interface EntityTypeComponentOverrides {
  /**
   * Replaces SummarySection's body. `null` = render no summary at all
   * (used by contact/company/deal/note — they have no separate summary
   * card, the overview carries everything).
   */
  summary?: ComponentType<{ object: CrmObject }> | null;
  /**
   * Replaces OverviewSection entirely. Useful when an entity has a
   * bespoke overview surface (e.g. `ahoy_note` → NoteRecordOverview which
   * renders the same NoteCard the notes tab uses in other slideouts).
   *
   * `editMode` / `onEditModeChange` are supplied by RecordBody ONLY when
   * the type sets `overviewHeaderEditLabel` (below): the slideout header's
   * edit button drives the override's edit mode through them, and the
   * override reports open/close back so the button can hide while editing.
   * Overrides that don't opt in receive neither and manage their own edit
   * lifecycle internally.
   */
  overview?: ComponentType<{
    object: CrmObject;
    editMode?: boolean;
    onEditModeChange?: (isEditMode: boolean) => void;
  }>;
  /**
   * Content rendered inside the RecordHeader's sub-header slot — typically
   * a row of identity metadata (e.g. job title + company for contacts,
   * stage + owner + company for deals). Returns null to render no
   * sub-header (the default for most types).
   */
  subHeader?: ComponentType<{ object: CrmObject }>;
  /**
   * The "type-specific" slideout section rendered between OverviewSection
   * and TimelineSection. Each entity type composes its own — for meetings
   * this composes the booking link + the meeting detail (AI prep brief);
   * for companies/deals it's the people list, etc.
   *
   * Receives `onRequestNoteCreation` as a pass-through callback so meeting
   * components can wire the "add a note" affordance to the parent
   * slideout's NotesSection. Components that don't use it ignore the prop.
   */
  typeSpecific?: ComponentType<{
    object: CrmObject;
    onRequestNoteCreation?: () => void;
  }>;
  /**
   * Ordered list of type-specific sections rendered in the same slot as
   * `typeSpecific` (between OverviewSection and TimelineSection). Preferred
   * over the single `typeSpecific` slot: entries are either declarative
   * related-entity sections (config-driven, rendered by `RelatedEntitySection`)
   * or `custom` bespoke components. When present, this takes precedence and
   * `typeSpecific` is ignored. See `relatedSections.ts`.
   */
  typeSpecificSections?: TypeSpecificSectionEntry[];
  /**
   * Section rendered immediately above the TimelineSection (after the
   * Notes/Files/Research sections). Meetings use it for the recurring-series
   * list, which reads best right before the activity timeline. The component
   * returns null when it has nothing to show.
   */
  beforeTimeline?: ComponentType<{ object: CrmObject }>;
  /**
   * The long-form "Documentation" section rendered at the bottom of the
   * Overview tab, for types whose slideout body is tabbed. The component
   * renders nothing when the record's `ahoy_documentation` property is
   * empty, so no empty section header appears.
   */
  documentation?: ComponentType<{ object: CrmObject }>;
  /**
   * Hover-preview body rendered inside the entity-badge popover. Receives
   * both the resolved object and an `orgId` because the preview components
   * fetch supplementary data scoped to the org (e.g. recent emails).
   *
   * Omitted slot ⇒ the badge's hover popover suppresses itself for that
   * entity type (matches the pre-registry behaviour where only contact
   * and company had hover bodies).
   */
  hoverBody?: ComponentType<{ object: CrmObject; orgId: number }>;
}

/**
 * Registry of all CRM entity types.
 * Add new entity types here to extend the system.
 */
const _mergedEntries = [
  CORE_ENTITY_TYPES,
  AGENT_ENTITY_TYPES,
  AUTOMATION_ENTITY_TYPES,
  WEB_ENTITY_TYPES,
  SYSTEM_ENTITY_TYPES,
  CREDENTIAL_ENTITY_TYPES,
].flatMap(m => Object.keys(m));
if (new Set(_mergedEntries).size !== _mergedEntries.length) {
  throw new Error(
    'entityTypeRegistry: duplicate entity type key across domain modules'
  );
}

export const ENTITY_TYPE_REGISTRY: Record<string, EntityTypeConfig> = {
  ...CORE_ENTITY_TYPES,
  ...AGENT_ENTITY_TYPES,
  ...AUTOMATION_ENTITY_TYPES,
  ...WEB_ENTITY_TYPES,
  ...SYSTEM_ENTITY_TYPES,
  ...CREDENTIAL_ENTITY_TYPES,
};

/** Fallback config for unregistered entity types */
export const FALLBACK_CONFIG: EntityTypeConfig = {
  displayName: 'Record',
  pluralDisplayName: 'Records',
  color: 'gray',
  timelineDefaultExpanded: false,
  bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
  getDisplayName,
  hiddenProperties: ['ahoy_summary'],
  routes: {
    list: '/:orgId/crm',
    detail: '/:orgId/crm/:entityType/:id',
  },
  tabs: ['overview', 'activity', 'notes', 'associations'],
};

/**
 * Get configuration for a entity type.
 *
 * @param entityType - CRM entity type (e.g., "ahoy_contact")
 * @returns Entity type configuration, or a generic fallback for unregistered types
 */
export function getEntityTypeConfig(entityType: string): EntityTypeConfig {
  return ENTITY_TYPE_REGISTRY[entityType] ?? FALLBACK_CONFIG;
}

/**
 * Check if a entity type is registered.
 *
 * @param entityType - CRM entity type to check
 * @returns True if registered, false otherwise
 */
export function isValidEntityType(entityType: string): boolean {
  return entityType in ENTITY_TYPE_REGISTRY;
}

/**
 * Get all registered entity types.
 *
 * @returns Array of entity type names
 */
export function getAllEntityTypes(): string[] {
  return Object.keys(ENTITY_TYPE_REGISTRY);
}

/**
 * Get create configuration for a entity type.
 *
 * @param entityType - CRM entity type (e.g., "ahoy_contact")
 * @returns Create configuration or null if not available
 */
export function getEntityTypeCreateConfig(
  entityType: string
): EntityTypeCreateConfig | null {
  const config =
    ENTITY_TYPE_REGISTRY[entityType as keyof typeof ENTITY_TYPE_REGISTRY];
  return config?.createConfig || null;
}

// Break the circular module-init cycle between this registry and
// hoverBodyDispatch. Static importing entityTypeRegistry from hoverBodyDispatch
// created: entityTypeRegistry → NoteRecordOverview → NoteCard → MarkdownContent
// → entityRendering → EntityBadgeById → HoverPreviewBadge → hoverBodyDispatch
// → entityTypeRegistry  (TDZ cycle — hangs Vitest module loader).
//
// Solution: hoverBodyDispatch no longer imports entityTypeRegistry; instead,
// this registry injects its getter once, here, after the module is fully
// evaluated. getEntityTypeConfig is a function declaration so it's hoisted
// and available immediately.
_setEntityTypeConfigGetter(getEntityTypeConfig);
